import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, X, ArrowRight } from 'lucide-react';
import './LandingPage.css';
import orgAdminImg from '../../assets/images/teamCollobaration.png'; // Using existing image
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';
import { useAuth } from '../../hooks/useAuth';
import { signInWithRedirect } from 'aws-amplify/auth';
import {
  clearConflictingAuthSessionForInvite,
  inviteEmailFromRecord,
  inviteRegistrationAutofillProps,
  resetInviteLandingStorage,
  storeInviteLandingContext,
} from '../../lib/orgInviteLanding';

const LandingOrgAdmin: React.FC = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const [invitation, setInvitation] = useState<any>(null);
  const [invitationLoading, setInvitationLoading] = useState(false);
  const [invitationError, setInvitationError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    organizationName: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isProcessingInvitation, setIsProcessingInvitation] = useState(false);

  const inviteEmail = inviteEmailFromRecord(invitation?.email);
  const registrationEmail = inviteEmail || inviteEmailFromRecord(form.email);

  // Google sign-in handler
  const handleGoogleSignIn = async () => {
    try {
      setError(null);
      
      const token = searchParams.get('token');
      if (token) {
        storeInviteLandingContext(token, 'org-admin');
      }
      
      await signInWithRedirect({ provider: 'Google', customState: 'prompt=select_account' });
    } catch (e) {
      const err = e as { message?: string };
      setError(err?.message || 'Google sign-in failed.');
    }
  };

  // Listen for authentication changes from Hub listener
  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const { getCurrentUser } = await import('aws-amplify/auth');
        const user = await getCurrentUser();
        if (user) {
          setIsAuthenticated(true);
          setIsProcessingInvitation(true);
          // User is authenticated, process invitation
          await processInvitationForAuthenticatedUser();
        }
      } catch (error) {
        setIsAuthenticated(false);
        // User not authenticated, show the registration form
      }
    };

    // Check auth status after a short delay to allow Hub listener to process
    const timer = setTimeout(checkAuthStatus, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Process invitation for already authenticated users
  const processInvitationForAuthenticatedUser = async () => {
    try {
      if (!invitation?.id) return;

      // Update invitation status to REGISTERED
      const client = generateClient<Schema>();
      await client.models.UserInvitation.update({
        id: invitation.id,
        status: 'REGISTERED'
      });
      const { getCurrentUser } = await import('aws-amplify/auth');
      let memberId = '';
      try {
        memberId = (await getCurrentUser()).userId;
      } catch {
        memberId = '';
      }
      void import('../../lib/careCircleNotifications').then(({ notifyOrgInviteAccepted }) =>
        notifyOrgInviteAccepted({
          organizationId: invitation.organizationId,
          organizationName: invitation.organizationName,
          newMemberUserId: memberId,
          referrerId: invitation.referrerId,
          actorUserId: memberId,
        }),
      );
      
      // Redirect based on role
      if (invitation.inviteeRole === 'PARENT') {
        // Redirect to kid profile if available
        if (invitation.kidProfileId) {
          navigate(`/kid-profile/${invitation.kidProfileId}`);
        } else {
          navigate('/kid-profile-form');
        }
      } else if (invitation.inviteeRole === 'ORG_ADMIN') {
        navigate('/org-admin-dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('❌ LandingOrgAdmin - Error processing invitation:', error);
      setError('Error processing invitation. Please try again.');
      setIsProcessingInvitation(false);
    }
  };

  // Fetch invitation details when component mounts
  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) return;

    resetInviteLandingStorage();
    storeInviteLandingContext(token, 'org-admin');
    fetchInvitationDetails(token);
    setShowForm(true);
  }, [searchParams]);

  // Set document title based on invitation role
  useEffect(() => {
    if (invitation?.inviteeRole) {
      const roleText = invitation.inviteeRole === 'PARENT' ? 'Parent' : 'Organization Admin';
      document.title = `Complete Your ${roleText} Registration - TinyWins`;
    } else {
      document.title = 'Complete Your Registration - TinyWins';
    }
  }, [invitation]);

  const fetchInvitationDetails = async (token: string) => {
    setInvitationLoading(true);
    setInvitationError(null);
    
    try {
      const client = generateClient<Schema>();
      const invitations = await client.models.UserInvitation.list({
        filter: {
          token: { eq: token },
          or: [
            { status: { eq: 'PENDING' } },
            { status: { eq: 'EMAIL_SENT' } }
          ]
        }
      });

      if (invitations.data && invitations.data.length > 0) {
        const invitationData = invitations.data[0];
        setInvitation(invitationData);
        const invitedEmail = inviteEmailFromRecord(invitationData.email);
        await clearConflictingAuthSessionForInvite(invitedEmail);

        setForm({
          firstName: '',
          lastName: '',
          email: invitedEmail,
          password: '',
          confirmPassword: '',
          phoneNumber: '',
          organizationName: invitationData.organizationName || '',
        });
      } else {
        setInvitationError('Invalid or expired invitation link.');
      }
    } catch (err) {
      console.error('Error fetching invitation details:', err);
      setInvitationError('Error loading invitation details.');
    } finally {
      setInvitationLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.id]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      // Store invitation token in localStorage before registration so register function can use it
      const token = searchParams.get('token');
      if (token && invitation) {
        storeInviteLandingContext(token, 'org-admin');
      }
      
      await register(
        registrationEmail,
        form.password,
        form.firstName,
        form.lastName,
        form.phoneNumber, // optional
        invitation?.organizationId,
        invitation?.kidProfileId,
        invitation?.inviteeRole // Pass role from invitation
      );

      // Update invitation status to REGISTERED
      if (invitation?.id) {
        try {
          const client = generateClient<Schema>();
          await client.models.UserInvitation.update({
            id: invitation.id,
            status: 'REGISTERED'
          });
        } catch (updateError) {
          console.error('Error updating invitation status:', updateError);
          // Don't fail registration if invitation update fails
        }
      }

      setSuccess(true);
      setForm({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        phoneNumber: '',
        organizationName: ''
      });
    } catch (err: any) {
      setError(err.message || 'Error creating account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="landing-page">
      {!showForm ? (
        <>
          <section className="hero relative z-10">
            <h1>Welcome, Organization Leader!</h1>
            <p className="subtitle">Empower your team and families with TinyWins.AI's collaborative platform.</p>
            <div className="flex gap-4 justify-center mt-8 relative z-20 ml-16">
              <button 
                className="px-10 py-5 bg-green-600 text-white text-2xl rounded-lg hover:bg-green-700 transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0 active:shadow-md font-bold"
                onClick={() => navigate('/org-onboard')}
              >
                Onboard Your Organization
              </button>
            </div>
            <img src={orgAdminImg} alt="Org Admin" className="landing-image mt-8 mx-auto" style={{maxWidth: 400}} />
          </section>
          <section className="info-section mt-12">
            <h2>Why Use TinyWins.AI for Your Organization?</h2>
            <ul className="feature-list">
              <li>Manage teams of parents, therapists, and staff</li>
              <li>Track progress and outcomes across your organization</li>
              <li>Centralize resources and communication</li>
            </ul>
          </section>
        </>
      ) : (
        <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-3xl p-8 lg:p-16 shadow-xl">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                  Complete Your Registration
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed">
                  {invitation?.inviteeRole === 'PARENT' 
                    ? "Set up your parent account and start tracking your child's development on TinyWins."
                    : "Set up your organization admin account and start managing your team on TinyWins."
                  }
                </p>
              </div>

              {/* Invitation Notice */}
              {invitation && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-800">
                        <strong>You've been invited!</strong> You're joining <strong>{invitation.organizationName}</strong> as a <strong>{invitation.inviteeRole === 'PARENT' ? 'Parent' : 'Organization Admin'}</strong>.
                      </p>
                      {invitation.kidProfileName && (
                        <p className="text-sm text-blue-700 mt-1">
                          <strong>Child Profile:</strong> {invitation.kidProfileName} (Age: {invitation.kidProfileAge})
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Invitation Error */}
              {invitationError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <X className="h-5 w-5 text-red-600" />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-800">{invitationError}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {invitationLoading && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-gray-800">Loading invitation details...</p>
                    </div>
                  </div>
                </div>
              )}

              {isProcessingInvitation ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-lg font-semibold text-gray-700">Processing your invitation...</p>
                  <p className="text-sm text-gray-500 mt-2">Please wait while we set up your account.</p>
                </div>
              ) : success ? (
                <div className="text-green-600 text-center font-semibold text-lg mb-4">
                  Account created successfully! Please check your email to verify your account.
                </div>
              ) : !isAuthenticated ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        value={form.firstName}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder="Enter your first name"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        value={form.lastName}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                        placeholder="Enter your last name"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="invite-email"
                      key={registrationEmail || 'invite-email-empty'}
                      value={registrationEmail}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Enter your email"
                      required
                      readOnly={!!inviteEmail}
                      {...inviteRegistrationAutofillProps}
                    />
                  </div>
                  <div>
                    <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                      Password
                    </label>
                    <input
                      type="password"
                      id="password"
                      value={form.password}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Create a password"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      value={form.confirmPassword}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Confirm your password"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="phoneNumber" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number (optional)
                    </label>
                    <input
                      type="tel"
                      id="phoneNumber"
                      value={form.phoneNumber}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="1234567890"
                      pattern="[0-9]{10}"
                      maxLength={10}
                    />
                  </div>
                  {error && <div className="text-red-600 text-center font-semibold text-sm mb-2">{error}</div>}
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-green-600 to-blue-600 text-white py-4 px-8 rounded-lg text-lg font-semibold hover:from-green-700 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl"
                    disabled={loading}
                  >
                    {loading ? 'Creating Account...' : invitation?.inviteeRole === 'PARENT' ? 'Complete Parent Registration' : 'Complete Organization Admin Registration'}
                    <ArrowRight className="inline-block ml-2 h-5 w-5" />
                  </button>
                  
                  {/* Google Sign-in Button */}
                  <div className="text-center my-4">
                    <span className="text-gray-500 text-sm">or</span>
                  </div>
                  
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    className="w-full bg-white border-2 border-gray-300 text-gray-700 py-4 px-8 rounded-lg text-lg font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
                    disabled={loading}
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </button>
                  
                  <p className="text-center text-sm text-gray-500 mt-4">
                    Free to get started • No credit card required • No waitlist
                  </p>
                </form>
              ) : null}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default LandingOrgAdmin; 
